'use client';

import { useRouterEventManager } from '@/react/providers/router-events/useRouterEventManager';
import ConfigurationService from '@/services/isomorphic/ConfigurationService';
import {
  useDeferredValue,
  useEffect,
  useLayoutEffect,
  type FC,
  type PropsWithChildren
} from 'react';
import { ErrorAlertContext } from '../ErrorAlertContext';
import { useErrorAlertProviderValue } from '../ErrorAlertContext/useErrorAlertProviderValue';

/**
 * This component provides the top-level context necessary for displaying error alerts.
 * It also sets up window-level event listeners to detect and display unhandled errors.
 */
export const GlobalErrorAlertProvider: FC<PropsWithChildren> = ({
  children
}) => {
  const value = useErrorAlertProviderValue();
  const { alert, closeAlert } = value;

  const events = useRouterEventManager();

  useLayoutEffect(
    function closeErrorOnNavigate() {
      events.on('routeChangeStart', closeAlert);
      return () => events.off('routeChangeStart', closeAlert);
    },
    [closeAlert, events]
  );

  /**
   * This event listener helps us to catch any error that happens outside of rendering,
   * such as within a event handler like `onClick`, and display it in our `ErrorAlert` popup.
   */
  useEffect(() => {
    const errorListener = (event: ErrorEvent | PromiseRejectionEvent): void => {
      // Temporarily hide uncaught errors in production
      // TODO: Eventually remove this after soft launch
      const hideUncaughtErrors = ConfigurationService.getConfig(
        'ui'
      ).getSetting('global.hideUncaughtErrors').value;

      if (hideUncaughtErrors) {
        return;
      }

      const alertContent =
        event instanceof ErrorEvent ? event.error : event.reason;

      alert(alertContent);
      event.preventDefault();
    };

    window.addEventListener('error', errorListener);
    window.addEventListener('unhandledrejection', errorListener);

    return () => {
      window.removeEventListener('error', errorListener);
      window.removeEventListener('unhandledrejection', errorListener);
    };
  }, [alert]);

  const deferredValue = useDeferredValue(value);

  return (
    <ErrorAlertContext.Provider value={deferredValue}>
      {children}
    </ErrorAlertContext.Provider>
  );
};
